import type { LotAnalysisReportData } from "../createPropertyReport";

export interface MeasurementKvRow {
  key: string;
  label: string;
  value: string;
}

function ftRound(v: number): string {
  return `${v.toFixed(0)} ft`;
}

function pct(v: number): string {
  return `${v.toFixed(0)}%`;
}

function thousands(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** Same rows as legacy `lotSection` (`Lot Analysis`), matching Figma `Measurements`. */
export function buildMeasurementRows(
  data: LotAnalysisReportData
): MeasurementKvRow[] {
  const frontageValue =
    data.roadFrontageFt > 0
      ? `${ftRound(data.roadFrontageFt)}${
          data.primaryRoadName ? ` on ${data.primaryRoadName}` : ""
        }`
      : "—";

  const buildable = data.bestBuildable;
  const bestBuildableValue = buildable
    ? `${thousands(buildable.areaSqFt)} ft² @ ${pct(buildable.avgSlopePercent)}${
        buildable.distanceToRoadFt != null
          ? ` · ${ftRound(buildable.distanceToRoadFt)} from road`
          : ""
      }`
    : "No qualifying region";

  const rows: MeasurementKvRow[] = [
    {
      key: "lotSize",
      label: "Lot size",
      value: `${data.acreage.toFixed(2)} ac · ${thousands(data.totalSqFt)} ft²`
    },
    {
      key: "perimeter",
      label: "Perimeter",
      value: ftRound(data.perimeterFt)
    },
    {
      key: "frontage",
      label: "Frontage",
      value: frontageValue
    },
    {
      key: "elevation",
      label: "Elevation",
      value: `${ftRound(data.minElevationFt)} → ${ftRound(
        data.maxElevationFt
      )} (Δ ${ftRound(data.elevationDropFt)})`
    },
    {
      key: "slope",
      label: "Slope across lot",
      value: `${pct(data.avgSlopePercent)} avg · ${pct(data.maxSlopePercent)} max`
    },
    {
      key: "bestBuildable",
      label: "Best buildable",
      value: bestBuildableValue
    }
  ];

  if (data.bestBuildableToRoadFt != null) {
    rows.push({
      key: "bestBuildableToRoad",
      label: "Best buildable → road",
      value: ftRound(data.bestBuildableToRoadFt)
    });
  }

  return rows;
}
